import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { apiJson } from '../lib/api'
import { useLayer } from './layers'
import { SkeletonRows, useSlow } from './pending'
import styles from './SearchPalette.module.css'

// Global search, ⌘K (h 465-485, 4058-4061). One query across Personnel,
// Picking jobs, Tags, Teams, Orchards and Reports, answered by GET
// /api/search in the active workspace only. The groups keep the server's
// fixed order; every row leads with its type, carries its type's
// disambiguating detail on a second line, and shows the matched text
// emphasised. Each group holds five rows and links to that page's own
// filtered list.
//
// While a search is out the last results stay; past the threshold they are
// replaced by "Searching…" and skeleton rows. The typed query is never
// cleared and the palette never closes itself: only Escape, the scrim or a
// chosen result closes it.

export const MIN_QUERY = 2
const DEBOUNCE_MS = 200

type Row = { id: string; title: string; [k: string]: any }
type Group = { type: string; label: string; total: number; results: Row[] }
type Item = { key: string; href: string; group: Group; row?: Row }

const qs = (o: Record<string, string>) => new URLSearchParams(o).toString()

// Where a result opens: the record itself where the page can open one, else
// the page's list filtered to it.
export function hrefOf(type: string, r: Row): string {
  switch (type) {
    case 'personnel':
      return `/personnel?${qs({ person: r.id })}`
    case 'jobs':
      return `/picking-jobs?${qs({ site: r.site_id, job: r.id })}`
    case 'tags':
      return `/product-activity?${qs({ q: r.title })}`
    case 'teams':
      return `/teams?${qs({ team: r.id })}`
    case 'orchards':
      return r.kind === 'site' ? `/sites-jobs?${qs({ site: r.site_id })}` : `/sites-jobs?${qs({ site: r.site_id, q: r.title })}`
    case 'reports':
      return r.kind === 'view' ? `/reports?${qs({ view: r.id })}` : `/reports?${qs({ report: r.report_key })}`
  }
  return '/'
}

// "See all in Personnel": the page's own list with its search box filled. The
// site-scoped pages (Picking jobs, Sites) open on the first result's site.
export function seeAllOf(g: Group, q: string): string {
  const site = g.results[0]?.site_id
  switch (g.type) {
    case 'personnel':
      return `/personnel?${qs({ q })}`
    case 'jobs':
      return `/picking-jobs?${qs(site ? { site, q } : { q })}`
    case 'tags':
      return `/product-activity?${qs({ q })}`
    case 'teams':
      return `/teams?${qs({ q })}`
    case 'orchards':
      return `/sites-jobs?${qs(site ? { site, q } : { q })}`
    case 'reports':
      return `/reports?${qs({ q })}`
  }
  return '/'
}

const dateText = (iso: string | null | undefined) => {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

// A block named "1" reads as a count on its own, so the word is added unless
// every name already carries it ("Block 3").
const blocksText = (blocks: string[] | undefined) => {
  if (!blocks?.length) return 'No blocks'
  if (blocks.every((b) => /^block\b/i.test(b))) return blocks.join(', ')
  return `${blocks.length === 1 ? 'Block' : 'Blocks'} ${blocks.join(', ')}`
}

// The second line (h 474-476): what tells two rows of one type apart.
export function detailOf(type: string, r: Row): string {
  const parts: (string | null | undefined | false)[] = []
  switch (type) {
    case 'personnel':
      parts.push(r.employee_id ? `ID ${r.employee_id}` : 'No worker ID', r.team_name || 'No team', r.preferred_name && `Goes by ${r.preferred_name}`, r.archived && 'Archived')
      break
    case 'jobs':
      parts.push(blocksText(r.blocks), r.created_at && `Created ${dateText(r.created_at)}`, r.site_name, r.archived && 'Archived')
      break
    case 'tags':
      parts.push(r.state, r.purpose, r.worker_name && `Assigned to ${r.worker_name}`)
      break
    case 'teams':
      parts.push(r.task, r.mode, !r.active && 'Inactive')
      break
    case 'orchards':
      parts.push(r.kind === 'site' ? 'Site' : r.parent_name ? `In ${r.parent_name}, ${r.site_name}` : `In ${r.site_name}`, r.region || 'No region', r.archived && 'Archived')
      break
    case 'reports':
      parts.push(r.kind === 'view' ? `Saved view of ${r.report_name}` : 'Report', r.live && 'Live')
      break
  }
  return parts.filter(Boolean).join(' · ')
}

// The row's mark and kind (V4 palette): the first letter on the type's tone,
// and what the row is, named on the right.
const TONE: Record<string, string> = { personnel: '#c67139', teams: '#7a8a5e', jobs: '#8c6b3f', tags: '#a2512a', orchards: '#5f6d45', reports: '#857c6d' }
const KIND: Record<string, string> = { personnel: 'Worker', teams: 'Team', jobs: 'Picking job', tags: 'Tag', reports: 'Report' }
function kindOf(g: Group, r: Row): string {
  if (g.type === 'orchards') return r.kind === 'site' ? 'Site' : 'Orchard'
  if (g.type === 'reports' && r.kind === 'view') return 'Saved view'
  return KIND[g.type] || g.label
}
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

// Every occurrence of the query in `text`, emphasised.
export function Marked({ text, q }: { text: string; q: string }) {
  const needle = q.trim().replace(/\s+/g, ' ').toLowerCase()
  if (!needle) return <>{text}</>
  const hay = text.toLowerCase()
  const out: React.ReactNode[] = []
  let at = 0
  for (let i = hay.indexOf(needle); i >= 0; i = hay.indexOf(needle, at)) {
    if (i > at) out.push(text.slice(at, i))
    out.push(<mark key={i}>{text.slice(i, i + needle.length)}</mark>)
    at = i + needle.length
  }
  out.push(text.slice(at))
  return <>{out}</>
}

// The rail's search row and the palette it opens. ⌘K or Ctrl+K opens it from
// anywhere, and focuses it again when it is already open.
export function GlobalSearch() {
  const [open, setOpen] = useState(false)
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen(true)
        input.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '')
  return (
    <>
      <button
        type="button"
        className={styles.railBtn}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-keyshortcuts={mac ? 'Meta+K' : 'Control+K'}
        title={`Search people, jobs, tags, teams, orchards and reports (${mac ? '⌘K' : 'Ctrl K'})`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <span className={styles.railText}>Search or jump to…</span>
      </button>
      {open && <SearchPalette inputRef={input} onClose={() => setOpen(false)} />}
    </>
  )
}

export function SearchPalette({ onClose, inputRef }: { onClose: () => void; inputRef?: React.RefObject<HTMLInputElement> }) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const box = useRef<HTMLDivElement>(null)
  const own = useRef<HTMLInputElement>(null)
  const field = inputRef || own
  const [q, setQ] = useState('')
  // What the results on screen answer, so a late reply to an older query
  // never replaces a newer one.
  const [shown, setShown] = useState<{ q: string; groups: Group[] } | null>(null)
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [active, setActive] = useState(0)
  const seq = useRef(0)
  const slow = useSlow(searching)
  const { requestClose } = useLayer(true, onClose, { el: box, modal: true, focusFirst: true })

  const term = q.trim().replace(/\s+/g, ' ')
  useEffect(() => {
    const mine = ++seq.current
    if (term.length < MIN_QUERY) {
      setSearching(false)
      setError(null)
      setShown(null)
      return
    }
    setSearching(true)
    const t = setTimeout(() => {
      apiJson<{ q: string; groups: Group[] }>(user, `/api/search?${qs({ q: term })}`)
        .then((res) => {
          if (mine !== seq.current) return
          setShown({ q: term, groups: res.groups || [] })
          setError(null)
          setActive(0)
        })
        .catch((e) => {
          if (mine !== seq.current) return
          setError(e?.message || 'Search failed.')
        })
        .finally(() => {
          if (mine === seq.current) setSearching(false)
        })
    }, DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [term, user])

  // Rows and "see all" links in the order the keyboard moves through them.
  const items = useMemo<Item[]>(() => {
    if (!shown) return []
    const out: Item[] = []
    for (const g of shown.groups) {
      for (const r of g.results) out.push({ key: `${g.type}:${r.kind || ''}:${r.id}`, href: hrefOf(g.type, r), group: g, row: r })
      if (g.total > g.results.length) out.push({ key: `${g.type}:all`, href: seeAllOf(g, shown.q), group: g })
    }
    return out
  }, [shown])

  const go = (it: Item | undefined) => {
    if (!it) return
    onClose()
    navigate(it.href)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!items.length) return
      const d = e.key === 'ArrowDown' ? 1 : -1
      setActive((a) => (a + d + items.length) % items.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      go(items[active])
    }
  }

  useEffect(() => {
    box.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView?.({ block: 'nearest' })
  }, [active])

  const replaced = searching && slow
  const idOf = (i: number) => `gsearch-${i}`
  let index = -1

  return createPortal(
    <div className={styles.scrim} onMouseDown={(e) => e.target === e.currentTarget && requestClose()}>
      <div ref={box} className={styles.box} role="dialog" aria-modal="true" aria-label="Search">
        <div className={styles.head}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            ref={field}
            className={styles.input}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search people, jobs, tags, teams, orchards, reports"
            aria-label="Search"
            role="combobox"
            aria-expanded={items.length > 0}
            aria-controls="gsearch-list"
            aria-activedescendant={items.length ? idOf(active) : undefined}
            maxLength={100}
            spellCheck={false}
            autoComplete="off"
          />
          <kbd className={styles.esc}>Esc</kbd>
        </div>

        <div className={styles.body} id="gsearch-list" role="listbox" aria-label="Results">
          {replaced ? (
            <>
              <div className={styles.note} role="status">
                Searching…
              </div>
              <SkeletonRows rowClass={styles.skel} cols={1} rows={5} label="Searching" />
            </>
          ) : error ? (
            <div className={styles.error} role="alert">
              The search did not run: {error} What you typed is still here; change it or try again.
            </div>
          ) : term.length < MIN_QUERY ? (
            <div className={styles.note}>Type at least {MIN_QUERY} characters. The search covers this workspace only.</div>
          ) : shown && shown.q === term && shown.groups.length === 0 ? (
            <div className={styles.note}>Nothing in this workspace matches “{term}”.</div>
          ) : (
            shown?.groups.map((g) => (
              <div key={g.type} className={styles.group} role="group" aria-label={g.label}>
                <div className={styles.groupHead}>
                  {g.label}
                  <span className={styles.count}>{g.total}</span>
                </div>
                {g.results.map((r) => {
                  const i = ++index
                  return (
                    <div
                      key={`${r.kind || ''}:${r.id}`}
                      id={idOf(i)}
                      data-index={i}
                      role="option"
                      aria-selected={i === active}
                      className={`${styles.row} ${i === active ? styles.on : ''}`}
                      onMouseMove={() => i !== active && setActive(i)}
                      onClick={() => go(items[i])}
                      data-testid="search-result"
                    >
                      <span className={styles.mark} style={{ background: TONE[g.type] || '#857c6d' }} aria-hidden="true">
                        {r.title.trim().charAt(0).toUpperCase()}
                      </span>
                      <div className={styles.text}>
                        <div className={styles.title}>
                          <Marked text={r.title} q={shown.q} />
                        </div>
                        <div className={styles.detail}>{detailOf(g.type, r)}</div>
                      </div>
                      <span className={styles.kind}>{kindOf(g, r)}</span>
                    </div>
                  )
                })}
                {g.total > g.results.length &&
                  (() => {
                    const i = ++index
                    return (
                      <div
                        id={idOf(i)}
                        data-index={i}
                        role="option"
                        aria-selected={i === active}
                        className={`${styles.all} ${i === active ? styles.on : ''}`}
                        onMouseMove={() => i !== active && setActive(i)}
                        onClick={() => go(items[i])}
                      >
                        See all {g.total} in {g.label}
                      </div>
                    )
                  })()}
              </div>
            ))
          )}
        </div>
        <div className={styles.foot}>
          <span>Up and down to move</span>
          <span>Enter to open</span>
          {shown && shown.q === term && !replaced && !error && (
            <span className={styles.footCount}>{plural(shown.groups.reduce((n, g) => n + g.total, 0), 'result', 'results')}</span>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
